/**
 * Which check-ins the client card's „Przypisz ankietę" offers for ONE client, which it refuses, and
 * what came of sending several at once.
 *
 * The backend's rules (`AssignCheckInAsync`): only a check-in is assigned this way, a paused one is
 * refused, one without a cadence is refused, and an assignment is idempotent. The intake is assigned
 * at onboarding and the lead form never, so neither is listed. Asked here before the request,
 * because each answer is knowable from the list the trainer is looking at.
 *
 * No React, no DOM, no query hook, no i18next: `t` arrives as a parameter.
 */

import { cadenceLabel, type SurveyGroups, type TranslateKey } from '~/entities/survey-template';

/** `open` may be ticked; every other state says why not. Precedence is the order written here. */
export type SurveyChoiceState = 'open' | 'assigned' | 'paused' | 'noCadence';

export interface SurveyChoiceRow {
  readonly id: number;
  readonly title: string;
  /** „co tydz · pon", or `null` for a check-in the wire carried no rhythm for. */
  readonly cadence: string | null;
  readonly state: SurveyChoiceState;
}

function stateOf(
  row: SurveyGroups['checkIns'][number],
  clientId: number,
): SurveyChoiceState {
  // „Przypisana" outranks „Wstrzymana": the client already has it, and that is the fact to read.
  if (row.assignedCustomerIds.includes(clientId)) return 'assigned';
  if (!row.active) return 'paused';
  if (row.cadence === null) return 'noCadence';
  return 'open';
}

/**
 * The check-ins, choosable ones first — each part in the list's own (collator) order — so what can be
 * picked is at the top and what cannot still says why.
 */
export function surveyChoiceRows(
  groups: SurveyGroups,
  clientId: number,
  t: TranslateKey,
): readonly SurveyChoiceRow[] {
  const rows = groups.checkIns.map((row) => ({
    id: row.id,
    title: row.title,
    cadence: cadenceLabel(row, t),
    state: stateOf(row, clientId),
  }));
  return [...rows.filter((row) => row.state === 'open'), ...rows.filter((row) => row.state !== 'open')];
}

/** A press on a row: an open one flips, a refused one leaves the selection as it was. */
export function toggleSurvey(
  selected: ReadonlySet<number>,
  id: number,
  rows: readonly SurveyChoiceRow[],
): ReadonlySet<number> {
  if (!rows.some((row) => row.id === id && row.state === 'open')) return selected;
  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/**
 * What „Przypisz (N)" sends: the ticked rows the list still offers. A tick on a row that has since
 * been assigned (by this dialog's own partial success) or paused elsewhere drops out rather than
 * being sent to a backend certain to refuse it.
 */
export function sendableSurveys(
  selected: ReadonlySet<number>,
  rows: readonly SurveyChoiceRow[],
): readonly SurveyChoiceRow[] {
  return rows.filter((row) => row.state === 'open' && selected.has(row.id));
}

export function assignLabel(count: number, assigning: boolean, t: TranslateKey): string {
  if (assigning) return t('web:assignSurvey.assigning');
  return count === 0 ? t('web:assignSurvey.assign') : t('web:assignSurvey.assignCount', { count });
}

/** One write's answer. */
export interface AssignResult {
  readonly title: string;
  readonly ok: boolean;
}

export type AssignOutcome =
  | { readonly kind: 'all'; readonly assigned: readonly string[] }
  | { readonly kind: 'partial'; readonly assigned: readonly string[]; readonly failed: readonly string[] }
  | { readonly kind: 'none'; readonly failed: readonly string[] };

export function assignOutcome(results: readonly AssignResult[]): AssignOutcome {
  const assigned = results.filter((result) => result.ok).map((result) => result.title);
  const failed = results.filter((result) => !result.ok).map((result) => result.title);
  if (failed.length === 0) return { kind: 'all', assigned };
  if (assigned.length === 0) return { kind: 'none', failed };
  return { kind: 'partial', assigned, failed };
}

/** What the host's live region says once every write landed. One form keeps the matrix's sentence. */
export function assignedSentence(client: string, assigned: readonly string[], t: TranslateKey): string {
  const [only] = assigned;
  return assigned.length === 1 && only !== undefined
    ? t('web:reports.matrix.assigned', { client, survey: only })
    : t('web:assignSurvey.assignedMany', { client, count: assigned.length });
}

/** The banner inside the dialog: which forms did not go, and how many did. `null` when all went. */
export function failureSentence(
  outcome: AssignOutcome,
  t: TranslateKey,
  locale: string,
): string | null {
  if (outcome.kind === 'all') return null;
  // The conjunction is the locale's (`Intl.ListFormat`), the quotes the copy's.
  const surveys = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(
    outcome.failed.map((title) => t('web:assignSurvey.quoted', { title })),
  );
  return outcome.kind === 'none'
    ? t('web:assignSurvey.assignFailed', { count: outcome.failed.length, surveys })
    : t('web:assignSurvey.partlyAssigned', {
        done: outcome.assigned.length,
        total: outcome.assigned.length + outcome.failed.length,
        count: outcome.failed.length,
        surveys,
      });
}
